/* Olivia theme — behaviour */
(function () {
  'use strict';

  const formatMoney = (cents) => {
    const format = window.moneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withSeparators = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withSeparators + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withSeparators)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  /* ------------------------------------------------------------------ *
   * Scroll reveal
   * ------------------------------------------------------------------ */
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -50px 0px' }
    );
    const watch = () => document.querySelectorAll('.scroll-trigger:not(.is-visible)').forEach((el) => observer.observe(el));
    document.addEventListener('DOMContentLoaded', watch);
    document.addEventListener('shopify:section:load', watch);
    watch();
  }

  /* ------------------------------------------------------------------ *
   * Sticky header
   * ------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (!this.classList.contains('header-wrapper--sticky')) return;
      this.style.position = 'sticky';
      this.style.top = '0';
      this.style.zIndex = '50';
      window.addEventListener(
        'scroll',
        () => this.classList.toggle('scrolled-past-header', window.scrollY > 60),
        { passive: true }
      );
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------ *
   * Header drawer / details modal — close on outside click & Escape
   * ------------------------------------------------------------------ */
  class DetailsToggle extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.close();
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.close();
      });
      const closeBtn = this.querySelector('.modal__close-button');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      const overlay = this.querySelector('.modal-overlay');
      if (overlay) overlay.addEventListener('click', () => this.close());
    }
    close() {
      this.details.removeAttribute('open');
      if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
    }
  }
  // a constructor can only be registered once, so each tag gets its own subclass
  customElements.define('header-drawer', class extends DetailsToggle {});
  customElements.define('details-modal', class extends DetailsToggle {});

  /* ------------------------------------------------------------------ *
   * Slider
   * ------------------------------------------------------------------ */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[id^="Slider-"]') || this.querySelector('.slider');
      if (!this.slider) return;
      this.prev = this.querySelector('button[name="previous"]');
      this.next = this.querySelector('button[name="next"]');
      this.currentEl = this.querySelector('.slider-counter--current');
      this.slides = Array.from(this.slider.children).filter((c) => !c.classList.contains('hidden'));
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.slider.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    }
    get index() {
      if (!this.slides.length) return 0;
      const position = this.slider.scrollLeft;
      return this.slides.reduce((nearest, slide, index) => {
        const target = slide.offsetLeft - this.slider.offsetLeft;
        const nearestTarget = this.slides[nearest].offsetLeft - this.slider.offsetLeft;
        return Math.abs(target - position) < Math.abs(nearestTarget - position) ? index : nearest;
      }, 0);
    }
    step(direction) {
      if (!this.slides.length) return;
      const target = Math.min(Math.max(this.index + direction, 0), this.slides.length - 1);
      this.goTo(target);
    }
    goTo(i) {
      const slide = this.slides[i];
      if (!slide) return;
      this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: 'smooth' });
    }
    update() {
      if (this.currentEl) this.currentEl.textContent = this.index + 1;
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
      this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: this.index } }));
    }
  }
  customElements.define('slider-component', SliderComponent);

  class SlideshowComponent extends SliderComponent {
    connectedCallback() {
      super.connectedCallback();
      this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      if (this.dataset.autoplay === 'true' && this.slides && this.slides.length > 1) {
        const speed = (parseFloat(this.dataset.speed) || 5) * 1000;
        this.timer = setInterval(() => {
          const next = this.index >= this.slides.length - 1 ? 0 : this.index + 1;
          this.goTo(next);
        }, speed);
        this.addEventListener('mouseenter', () => clearInterval(this.timer));
      }
    }
    update() {
      super.update();
      if (this.dots) this.dots.forEach((d, i) => d.classList.toggle('slider-counter__link--active', i === this.index));
      if (this.prev) this.prev.disabled = false;
      if (this.next) this.next.disabled = false;
    }
    step(direction) {
      if (!this.slides.length) return;
      let target = this.index + direction;
      if (target < 0) target = this.slides.length - 1;
      if (target > this.slides.length - 1) target = 0;
      this.goTo(target);
    }
  }
  customElements.define('slideshow-component', SlideshowComponent);

  /* ------------------------------------------------------------------ *
   * Product media gallery + lightbox
   * ------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[id^="GalleryViewer-"]');
      this.thumbnails = this.querySelector('[id^="GalleryThumbnails-"]');
      if (!this.viewer) return;

      if (this.thumbnails) {
        this.thumbnails.querySelectorAll('.thumbnail').forEach((button) => {
          button.addEventListener('click', () => this.setActive(button.dataset.target));
        });
        this.viewer.addEventListener('slider:change', () => this.syncThumbnail());
      }

      this.querySelectorAll('.product__media-toggle').forEach((button) => {
        button.addEventListener('click', () => {
          const modal = document.querySelector(`#ProductModal-${this.id.replace('MediaGallery-', '')}`);
          if (modal) modal.show(button.dataset.mediaId);
        });
      });
    }
    setActive(target) {
      const slide = this.viewer.querySelector(`[data-media-id="${target}"]`);
      if (!slide) return;
      const list = this.viewer.querySelector('.product__media-list');
      list.scrollTo({ left: slide.offsetLeft - list.offsetLeft, behavior: 'smooth' });
      this.markThumbnail(target);
    }
    syncThumbnail() {
      const list = this.viewer.querySelector('.product__media-list');
      const slides = Array.from(list.children);
      const index = slides.reduce((nearest, slide, currentIndex) => {
        const target = slide.offsetLeft - list.offsetLeft;
        const nearestTarget = slides[nearest].offsetLeft - list.offsetLeft;
        return Math.abs(target - list.scrollLeft) < Math.abs(nearestTarget - list.scrollLeft) ? currentIndex : nearest;
      }, 0);
      const slide = list.children[index];
      if (slide) this.markThumbnail(slide.dataset.mediaId);
    }
    markThumbnail(target) {
      this.querySelectorAll('.thumbnail').forEach((t) => t.classList.toggle('thumbnail--active', t.dataset.target === target));
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class ProductModal extends HTMLElement {
    connectedCallback() {
      const close = this.querySelector('.product-media-modal__toggle');
      if (close) close.addEventListener('click', () => this.hide());
      this.addEventListener('click', (e) => {
        if (e.target === this || e.target.classList.contains('product-media-modal__dialog')) this.hide();
      });
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.hide();
      });
    }
    show(mediaId) {
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      const target = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (target) target.scrollIntoView({ block: 'center' });
    }
    hide() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  }
  customElements.define('product-modal', ProductModal);

  /* ------------------------------------------------------------------ *
   * Add to cart — handles bundle tier + upsell in one request
   * ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.root = this.closest('product-info') || document;
      this.tierSelector = this.root.querySelector('[data-tiered-selector]');
      if (this.tierSelector) {
        this.tierVariants = this.readTierVariants();
        this.tierSelector.querySelectorAll('[data-tier-option-group]').forEach((group) => {
          group.querySelectorAll('[data-tier-option-value]').forEach((valueButton) => {
            valueButton.addEventListener('click', () => this.selectTierOption(group, valueButton));
          });
        });
        this.tierSelector.querySelectorAll('.tiered-option').forEach((option) => {
          option.addEventListener('click', () => this.selectTier(option));
        });
        const active = this.tierSelector.querySelector('.tiered-option--active');
        if (active) this.selectTier(active);
      }
    }

    readTierVariants() {
      const node = this.tierSelector.querySelector('[data-tier-variants-json]');
      if (!node) return [];
      try {
        const variants = JSON.parse(node.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.warn('Paket varyantları okunamadı.', error);
        return [];
      }
    }

    selectTierOption(group, valueButton) {
      group.querySelectorAll('[data-tier-option-value]').forEach((button) => {
        const active = button === valueButton;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const activeTier = this.tierSelector.querySelector('.tiered-option--active');
      if (activeTier) this.selectTier(activeTier);
    }

    selectedTierOptionValues() {
      const selected = new Map();
      this.tierSelector.querySelectorAll('[data-tier-option-group]').forEach((group) => {
        const active = group.querySelector('[data-tier-option-value].is-active');
        if (active) selected.set(Number(group.dataset.optionIndex), active.dataset.tierOptionValue);
      });
      return selected;
    }

    resolveTierVariant(option) {
      if (option.dataset.tierSource !== 'product-variant' || !this.tierVariants.length) return null;

      const units = Number(option.dataset.tierUnits);
      const packagePattern = new RegExp(`(^|\\D)${units}\\s*adet(\\D|$)`, 'i');
      const selectedOptions = this.selectedTierOptionValues();

      return this.tierVariants.find((variant) => {
        const packageMatches = (variant.options || []).some((value) => packagePattern.test(String(value)));
        if (!packageMatches) return false;
        return Array.from(selectedOptions.entries()).every(([index, value]) => variant.options?.[index] === value);
      }) || null;
    }

    selectTier(option) {
      this.tierSelector.querySelectorAll('.tiered-option').forEach((o) => {
        o.classList.remove('tiered-option--active');
        o.setAttribute('aria-pressed', 'false');
      });
      option.classList.add('tiered-option--active');
      option.setAttribute('aria-pressed', 'true');

      const resolvedVariant = this.resolveTierVariant(option);
      if (resolvedVariant) {
        option.dataset.tierVariant = resolvedVariant.id;
        option.dataset.tierQuantity = 1;
        option.dataset.tierPrice = resolvedVariant.price;
        option.dataset.tierCompare = resolvedVariant.compare_at_price || 0;
        option.dataset.tierAvailable = String(resolvedVariant.available);
        const finalPrice = option.querySelector('.tiered-option__final-price');
        const comparePrice = option.querySelector('.tiered-option__compare-price');
        const unitPrice = option.querySelector('[data-dynamic-unit-price]');
        if (finalPrice) finalPrice.textContent = formatMoney(resolvedVariant.price);
        if (unitPrice) unitPrice.textContent = `${formatMoney(Math.round(resolvedVariant.price / Number(option.dataset.tierUnits)))} / adet`;
        if (comparePrice) {
          comparePrice.textContent = formatMoney(resolvedVariant.compare_at_price || 0);
          comparePrice.hidden = !(resolvedVariant.compare_at_price > resolvedVariant.price);
        }
      }

      const qtyInput = this.form.querySelector('[name="quantity"]');
      if (qtyInput) qtyInput.value = option.dataset.tierQuantity || 1;
      const idInput = this.form.querySelector('.product-variant-id');
      if (idInput && option.dataset.tierVariant) idInput.value = option.dataset.tierVariant;

      this.unavailable = !resolvedVariant && option.dataset.tierSource === 'product-variant'
        ? true
        : option.dataset.tierAvailable !== 'true';
      if (idInput) idInput.disabled = this.unavailable;
      if (this.submitButton) {
        this.submitButton.disabled = this.unavailable;
        this.submitButton.setAttribute('aria-disabled', String(this.unavailable));
      }

      const price = parseInt(option.dataset.tierPrice, 10);
      const compare = parseInt(option.dataset.tierCompare, 10);
      this.updatePrices(price, compare);
    }

    updatePrices(price, compare) {
      if (isNaN(price)) return;
      this.root.querySelectorAll('[data-sticky-price] .price, .product__price .price').forEach((el) => {
        const onSale = compare > price;
        el.classList.toggle('price--on-sale', onSale);
        el.querySelectorAll('.price-item--regular').forEach((n) => {
          n.textContent = n.classList.contains('price-item--strikethrough') ? formatMoney(compare) : formatMoney(price);
        });
        el.querySelectorAll('.price-item--sale').forEach((n) => (n.textContent = formatMoney(price)));
      });
    }

    get items() {
      const idInput = this.form.querySelector('.product-variant-id');
      const qtyInput = this.form.querySelector('[name="quantity"]');
      const items = [{ id: Number(idInput.value), quantity: Number(qtyInput ? qtyInput.value : 1) || 1 }];
      this.root.querySelectorAll('[data-upsell] .lumin-upsell__checkbox:checked').forEach((box) => {
        items.push({ id: Number(box.dataset.upsellVariant), quantity: 1 });
      });
      return items;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.showError('');

      try {
        const response = await fetch(window.shopUrls.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: this.items })
        });
        const data = await response.json();
        if (data.status) {
          this.showError(data.description || data.message);
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { items: data.items || [data] } }));
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('loading', state);
      const disabled = state || this.unavailable;
      this.submitButton.disabled = Boolean(disabled);
      this.submitButton.setAttribute('aria-disabled', String(Boolean(disabled)));
      const spinner = this.submitButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !state);
    }

    showError(message) {
      if (!this.errorWrapper) return;
      this.errorWrapper.hidden = !message;
      const target = this.errorWrapper.querySelector('.product-form__error-message');
      if (target) target.textContent = message || '';
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------ *
   * Sticky ATC bar
   * ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', () => {
    const bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;
    const anchor = document.querySelector('.product-form__buttons');
    if (!anchor) return;

    bar.querySelectorAll('.sticky-atc-trigger').forEach((button) => {
      button.addEventListener('click', () => {
        const form = document.getElementById(button.dataset.formId);
        if (form) form.requestSubmit ? form.requestSubmit() : form.querySelector('[type="submit"]').click();
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0)),
        { threshold: 0 }
      );
      observer.observe(anchor);
    }
  });

  /* ------------------------------------------------------------------ *
   * Cart notification / count refresh
   * ------------------------------------------------------------------ */
  class CartNotification extends HTMLElement {
    connectedCallback() {
      this.notification = this.querySelector('#cart-notification');
      this.productWrapper = this.querySelector('#cart-notification-product');
      const closeBtn = this.querySelector('.cart-notification__close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      document.addEventListener('cart:added', (event) => this.open(event.detail.items));
    }
    open(items) {
      if (this.productWrapper && items && items.length) {
        this.productWrapper.innerHTML = items
          .map(
            (item) =>
              `<div class="cart-notification__product">${
                item.image ? `<img src="${item.image}" alt="" width="60" loading="lazy">` : ''
              }<div><div>${item.product_title || item.title || ''}</div><div>${formatMoney(
                item.final_line_price != null ? item.final_line_price : item.line_price || item.price || 0
              )}</div></div></div>`
          )
          .join('');
      }
      if (this.notification) this.notification.classList.add('active');
      this.refreshCount();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.close(), 8000);
    }
    close() {
      if (this.notification) this.notification.classList.remove('active');
    }
    async refreshCount() {
      try {
        const res = await fetch(window.shopUrls.cart + '.js');
        const cart = await res.json();
        const bubbleParent = document.getElementById('cart-icon-bubble');
        if (!bubbleParent) return;
        let bubble = bubbleParent.querySelector('.cart-count-bubble');
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubbleParent.appendChild(bubble);
        }
        bubble.innerHTML = `<span aria-hidden="true">${cart.item_count}</span>`;
      } catch (e) {
        /* non-fatal */
      }
    }
  }
  customElements.define('cart-notification', CartNotification);

  /* ------------------------------------------------------------------ *
   * Order countdown ("order within X → delivered by Y")
   * ------------------------------------------------------------------ */
  const initCountdowns = () => {
    document.querySelectorAll('.order-countdown').forEach((el) => {
      const cutoff = parseInt(el.dataset.cutoffHour, 10);
      const minDays = parseInt(el.dataset.deliveryMin, 10) || 1;
      const maxDays = parseInt(el.dataset.deliveryMax, 10) || 3;
      const timeEl = el.querySelector('[data-countdown-timer]');
      const dayEl = el.querySelector('[data-shipping-day]');
      if (!timeEl) return;

      const locale = document.documentElement.lang || 'tr';

      const tick = () => {
        const now = new Date();
        const target = new Date(now);
        target.setHours(cutoff, 0, 0, 0);
        if (target <= now) target.setDate(target.getDate() + 1);
        let diff = Math.floor((target - now) / 1000);
        const h = String(Math.floor(diff / 3600)).padStart(2, '0');
        const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
        const s = String(diff % 60).padStart(2, '0');
        timeEl.textContent = `${h}:${m}:${s}`;

        if (dayEl) {
          const from = new Date(now);
          from.setDate(from.getDate() + minDays);
          const to = new Date(now);
          to.setDate(to.getDate() + maxDays);
          const fmt = (d) => d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
          dayEl.textContent = minDays === maxDays ? fmt(from) : `${fmt(from)} - ${fmt(to)}`;
        }
      };
      tick();
      setInterval(tick, 1000);
    });
  };
  document.addEventListener('DOMContentLoaded', initCountdowns);
  document.addEventListener('shopify:section:load', initCountdowns);

  /* ------------------------------------------------------------------ *
   * UGC video story slider
   * ------------------------------------------------------------------ */
  class VideoStorySlider extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('.video-story__container');
      this.slides = Array.from(this.querySelectorAll('.video-story__slide'));
      if (!this.slides.length) return;
      this.index = 0;
      this.nav = this.querySelector('.video-story__nav');
      this.modal = this.querySelector('.video-story__modal');
      this.modalVideo = this.querySelector('.video-story__modal-video');

      this.querySelector('.video-story__arrow--prev').addEventListener('click', () => this.move(-1));
      this.querySelector('.video-story__arrow--next').addEventListener('click', () => this.move(1));

      this.slides.forEach((slide, i) => {
        slide.addEventListener('click', () => this.openModal(i));
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'video-story__dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', String(i + 1));
        dot.addEventListener('click', () => this.goTo(i));
        this.nav.appendChild(dot);
      });

      this.querySelector('.video-story__modal-close').addEventListener('click', () => this.closeModal());
      this.querySelector('.video-story__modal-overlay').addEventListener('click', () => this.closeModal());
      this.querySelector('.video-story__modal-prev').addEventListener('click', () => this.openModal(this.modalIndex - 1));
      this.querySelector('.video-story__modal-next').addEventListener('click', () => this.openModal(this.modalIndex + 1));
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.closeModal();
      });

      this.render();
      window.addEventListener('resize', () => this.render());
    }

    get perView() {
      const value = getComputedStyle(this).getPropertyValue('--video-columns');
      return parseInt(value, 10) || 1;
    }

    move(direction) {
      this.goTo(this.index + direction);
    }

    goTo(i) {
      const max = Math.max(0, this.slides.length - this.perView);
      this.index = Math.min(Math.max(i, 0), max);
      this.render();
    }

    render() {
      const slideWidth = this.slides[0].getBoundingClientRect().width + 10;
      this.container.style.transform = `translateX(${-this.index * slideWidth}px)`;
      this.nav.querySelectorAll('.video-story__dot').forEach((dot, i) => dot.classList.toggle('active', i === this.index));
    }

    openModal(i) {
      if (i < 0) i = this.slides.length - 1;
      if (i >= this.slides.length) i = 0;
      this.modalIndex = i;
      const source = this.slides[i].querySelector('video');
      if (!source) return;
      const clone = source.cloneNode(true);
      clone.muted = false;
      clone.controls = true;
      clone.autoplay = true;
      clone.loop = false;
      clone.removeAttribute('hidden');
      this.modalVideo.innerHTML = '';
      this.modalVideo.appendChild(clone);
      this.modal.hidden = false;
      document.body.classList.add('overflow-hidden');
      clone.play().catch(() => {});
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      this.modalVideo.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
    }
  }
  customElements.define('video-story-slider', VideoStorySlider);

  class QuickReviewSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-quick-review-slide]'));
      if (!this.slides.length) return;
      this.index = 0;
      this.querySelector('.quick-review-slider--prev')?.addEventListener('click', () => this.show(this.index - 1));
      this.querySelector('.quick-review-slider--next')?.addEventListener('click', () => this.show(this.index + 1));
      this.show(0);
    }

    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== this.index;
      });
    }
  }
  customElements.define('quick-review-slider', QuickReviewSlider);

  /* ------------------------------------------------------------------ *
   * Before / after comparison slider
   * ------------------------------------------------------------------ */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.after = this.querySelector('[data-after]');
      this.handle = this.querySelector('[data-handle]');
      if (!this.after || !this.handle) return;
      this.setPosition(parseFloat(this.dataset.start || 50));

      const onMove = (clientX) => {
        const rect = this.getBoundingClientRect();
        this.setPosition(((clientX - rect.left) / rect.width) * 100);
      };

      let dragging = false;
      const start = () => (dragging = true);
      const stop = () => (dragging = false);

      this.addEventListener('mousedown', (e) => {
        start();
        onMove(e.clientX);
      });
      window.addEventListener('mousemove', (e) => dragging && onMove(e.clientX));
      window.addEventListener('mouseup', stop);
      this.addEventListener('touchstart', (e) => {
        start();
        onMove(e.touches[0].clientX);
      }, { passive: true });
      window.addEventListener('touchmove', (e) => dragging && onMove(e.touches[0].clientX), { passive: true });
      window.addEventListener('touchend', stop);
      this.handle.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.setPosition(this.position - 4);
        if (e.key === 'ArrowRight') this.setPosition(this.position + 4);
      });
    }
    setPosition(percent) {
      this.position = Math.min(Math.max(percent, 0), 100);
      this.style.setProperty('--split-position', this.position + '%');
    }
  }
  customElements.define('before-after', BeforeAfter);

  /* ------------------------------------------------------------------ *
   * Lightbox grid (customer gallery)
   * ------------------------------------------------------------------ */
  class LightboxGallery extends HTMLElement {
    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('[data-lightbox-src]'));
      if (!this.images.length) return;
      this.modal = this.querySelector('.lightbox__modal');
      this.modalImage = this.querySelector('.lightbox__image');
      this.images.forEach((item, i) => item.addEventListener('click', () => this.open(i)));
      this.querySelector('.lightbox__close').addEventListener('click', () => this.close());
      this.querySelector('.lightbox__prev').addEventListener('click', () => this.open(this.index - 1));
      this.querySelector('.lightbox__next').addEventListener('click', () => this.open(this.index + 1));
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open(i) {
      if (i < 0) i = this.images.length - 1;
      if (i >= this.images.length) i = 0;
      this.index = i;
      this.modalImage.src = this.images[i].dataset.lightboxSrc;
      this.modal.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }
    close() {
      this.modal.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }
  }
  customElements.define('lightbox-gallery', LightboxGallery);

  /* ------------------------------------------------------------------ *
   * Infinite marquee (logo bar / review strip)
   * ------------------------------------------------------------------ */
  class MarqueeScroller extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.marquee__track');
      if (!track || track.dataset.cloned) return;
      track.dataset.cloned = 'true';
      track.innerHTML += track.innerHTML;
    }
  }
  customElements.define('marquee-scroller', MarqueeScroller);
})();
